import React, { useState, useEffect, useCallback } from 'react';
import { Header } from '../layout/Header';
import { useCalculatorStore } from '../../hooks/useCalculatorStore';
import { Calculator } from '../calculator/Calculator';
import { LoadingScreen } from '../ui/LoadingScreen';
import { NetworkQualityService } from '../../services/performance/NetworkQualityService';
import type { ConnectionQuality } from '../../services/performance/NetworkQualityService';

interface AuditorLayoutProps {
  children: React.ReactNode;
}

interface LoadingStep {
  id: string;
  label: string;
  status: 'pending' | 'loading' | 'completed' | 'error';
}

/**
 * Layout simplifié pour la page de liste des clients auditeurs.
 * Pas de sidebar (elle serait vide), pas de chat (pas de contexte entreprise).
 * Quand l'auditeur entre dans une entreprise, le ProtectedRoute utilise
 * le Layout standard avec Sidebar complète (exercices, hiérarchie, chat, etc.).
 */
export function AuditorLayout({ children }: AuditorLayoutProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [connectionQuality, setConnectionQuality] = useState<ConnectionQuality>('good');
  const [loadingSteps, setLoadingSteps] = useState<LoadingStep[]>([
    { id: 'auth', label: "Vérification de l'authentification", status: 'pending' },
    { id: 'config', label: 'Chargement de la configuration', status: 'pending' },
    { id: 'data', label: 'Préparation des données', status: 'pending' },
    { id: 'ui', label: "Initialisation de l'interface", status: 'pending' },
  ]);

  const isCalculatorOpen = useCalculatorStore(state => state.isOpen);

  const updateStep = useCallback((stepId: string, status: LoadingStep['status']) => {
    setLoadingSteps(prev => prev.map(step =>
      step.id === stepId ? { ...step, status } : step
    ));
  }, []);

  useEffect(() => {
    const unsubscribe = NetworkQualityService.subscribe((info) => {
      setConnectionQuality(info.quality);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    const simulateLoading = async () => {
      const quality = NetworkQualityService.getQuality();
      const delays = {
        excellent: { base: 200, step: 150 },
        good: { base: 300, step: 200 },
        moderate: { base: 500, step: 300 },
        slow: { base: 800, step: 500 },
        offline: { base: 1000, step: 600 },
      };
      const { base, step } = delays[quality] || delays.good;

      updateStep('auth', 'loading');
      setLoadingProgress(10);
      await new Promise(r => setTimeout(r, base));
      updateStep('auth', 'completed');
      setLoadingProgress(25);

      updateStep('config', 'loading');
      await new Promise(r => setTimeout(r, step));
      updateStep('config', 'completed');
      setLoadingProgress(50);

      updateStep('data', 'loading');
      await new Promise(r => setTimeout(r, step));
      updateStep('data', 'completed');
      setLoadingProgress(75);

      updateStep('ui', 'loading');
      await new Promise(r => setTimeout(r, step));
      updateStep('ui', 'completed');
      setLoadingProgress(100);

      await new Promise(r => setTimeout(r, 200));
      setIsLoading(false);
    };
    simulateLoading();
  }, [updateStep]);

  if (isLoading) {
    return (
      <LoadingScreen
        showProgress
        progress={loadingProgress}
        steps={loadingSteps}
        connectionQuality={connectionQuality}
      />
    );
  }

  // Noop for sidebar toggle — no sidebar on clients page
  const handleSidebarToggle = () => {};

  return (
    <div className="min-h-screen bg-background animate-fade-in">
      <Header onToggleSidebar={handleSidebarToggle} />

      <div className="flex pt-16">
        <main className="flex-1 p-4 md:p-6 overflow-auto h-[calc(100vh-4rem)]">
          <div className="max-w-7xl mx-auto space-y-4 animate-fade-in-up">
            {children}
          </div>
        </main>
      </div>

      {isCalculatorOpen && <Calculator />}
    </div>
  );
}
